import FormStyleProvider from "@client/content/Providers/FormStyle/container";
import { END_BUTTONS_STYLE } from "@client/content/controls/Form/lib";
import { getEntityRef, getRootContainerProps } from "@client/content/lib/helper";
import { ModuleButton, ModuleSecondaryButton } from "@client/content/modules/Button";
import { type ModuleApiInfo } from "@client/content/selectors";
import { type RootContainerProps } from "@client/content/types";
import { FormField } from "@client/controls/Form/Field/component";
import ButtonsContainer from "@common/controls/Container/Buttons";
import { type FormControlledRenderParams } from "@common/controls/Form/component";
import Select from "@common/controls/Inputs/Select/component";
import { TextInput } from "@common/controls/Inputs/Text";
import { FormModalControlled } from "@common/controls/Modal/Modals/Form/component";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import * as React from "react";
import { type MemberWallet } from "../types";

export type MapProps = RootContainerProps & {
  accounts: MemberWallet.View.Account[];
  apiInfo: ModuleApiInfo;
  currencies: MemberWallet.Currency[];
  detail: MemberWallet.View.Account | null;
  error: string | null;
  isVisible: boolean;
  status: MemberWallet.Status;
};

export type ActionProps = {
  onClose: () => void;
  onSubmit: (data: MemberWallet.View.AccountSettings, isAdd: boolean) => void;
};

export type Props = ActionProps & MapProps;
export default (props: Props) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const currencyOptions = React.useMemo(
    () => props.currencies.filter((x) => x.isCommission).map((x) => ({ label: x.currencyCode, value: x.currencyCode })),
    [props.currencies]
  );

  const defaultValues = React.useMemo(() => props.detail ?? { isPreferred: false }, [props.detail]);

  return (
    <>
      <FormModalControlled
        submit={(data) => props.onSubmit(data, !props.detail)}
        title={props.detail ? i18n.settings : i18n.addAccount}
        isVisible={props.isVisible}
        defaultValues={defaultValues}
        width="400px"
        errorMessage={props.error}
        onClose={props.onClose}
      >
        {({ values, isSubmitting, submit }: FormControlledRenderParams<MemberWallet.View.AccountSettings>) => (
          <FormStyleProvider {...getEntityRef(props)}>
            {!props.detail && (
              <FormField
                label={i18n.currency}
                Component={Select}
                componentProps={{
                  options: currencyOptions,
                  sort: "asc",
                  sortType: "natural",
                  excludeOptionValues: props.accounts.map((x) => x.currencyCode)
                }}
                isRequired
                name="currencyCode"
                value={values.currencyCode}
              />
            )}

            <FormField
              label={i18n.commissionAccount}
              Component={Select}
              componentProps={{ options: options.yesNo() }}
              isRequired
              name="isPreferred"
              value={values.isPreferred}
              tooltip={i18n.infoAffiliateCommissionsAccount}
            />

            {props.detail && (
              <FormField
                Component={TextInput}
                componentProps={{ isReadonly: true }}
                isRequired
                label={i18n.accountNumber}
                name="accountNumber"
                value={values.accountNumber}
              />
            )}

            <ButtonsContainer style={END_BUTTONS_STYLE}>
              <ModuleSecondaryButton {...getRootContainerProps(props)} onClick={props.onClose} caption={i18n.cancel} />
              <ModuleButton
                {...getRootContainerProps(props)}
                isDisabled={isSubmitting || props.status === "Creating" || props.status === "Updating"}
                onClick={submit}
                caption={i18n.submit}
              />
            </ButtonsContainer>
          </FormStyleProvider>
        )}
      </FormModalControlled>
    </>
  );
};
